// src/services/healthCheck.js
// Single source of truth for connection state: ONLINE or OFFLINE — nothing else.
// ONLINE iff GET /api/health returns 200 "ok". ANY other result (maintenance, degraded,
// DB/ERP down, unreachable, timeout) => OFFLINE, applied immediately (no debounce).

import { ref } from 'vue';

export const isOnline = ref(true);

let intervalMs = 30_000;
let inactivityLockMinutes = 5;   // delivered by backend; kept for a future lock screen
let timerId = null;

const listeners = { online: [], offline: [] };
export function onOnline(fn) { listeners.online.push(fn); }
export function onOffline(fn) { listeners.offline.push(fn); }
export function getInactivityLockMinutes() { return inactivityLockMinutes; }

// The terminal's branch — lets /api/health apply per-branch state server-side.
function storeId() { return localStorage.getItem('branchId') || ''; }

function setOnline(next) {
  if (isOnline.value === next) return;      // only fire listeners on a real transition
  isOnline.value = next;
  (next ? listeners.online : listeners.offline).forEach(fn => {
    try { fn(); } catch { /* listener errors shouldn't break the loop */ }
  });
}

async function ping() {
  try {
    const res = await fetch('/api/health', {
      cache: 'no-store',
      headers: { 'store-id': storeId() },
    });

    if (res.ok) {
      const data = await res.json();
      if (data.heartbeatIntervalSeconds) intervalMs = data.heartbeatIntervalSeconds * 1000;
      if (data.inactivityLockMinutes) inactivityLockMinutes = data.inactivityLockMinutes;
      setOnline(true);
    } else {
      // Not "ok" (maintenance / degraded / DB or ERP down) => offline, no exceptions.
      try {
        const body = await res.json();
        console.info('[health] offline —', body.status, body.reason);
      } catch { /* non-JSON */ }
      setOnline(false);
    }
  } catch {
    setOnline(false);   // unreachable / timeout
  }
}

// Recursive setTimeout so intervalMs is read fresh each cycle.
async function scheduleNext() {
  await ping();
  timerId = setTimeout(scheduleNext, intervalMs);
}

export function startHealthCheck() {
  if (timerId) return;
  scheduleNext(); // immediate first ping

  window.addEventListener('online', ping);
  window.addEventListener('offline', () => setOnline(false));
}

export function stopHealthCheck() {
  clearTimeout(timerId);
  timerId = null;
}

// Force an immediate re-check.
export function pingNow() { return ping(); }
